/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import { useEffect, useRef, useState } from "react";

type UseAnimatedStreamingTextOptions = {
  text: string;
  enabled?: boolean;
  tickMs?: number;
};

const splitIntoTextTokens = (text: string): string[] => {
  return text.match(/\s*\S+\s*|\s+/g) ?? [];
};

export function useAnimatedStreamingText({
  text,
  enabled = true,
  tickMs = 45,
}: UseAnimatedStreamingTextOptions): string {
  const [visibleText, setVisibleText] = useState("");

  const fullTextRef = useRef("");
  const visibleTextRef = useRef("");
  const tokenQueueRef = useRef<string[]>([]);
  const timerRef = useRef<number | null>(null);

  const setVisibleStreamedText = (nextText: string) => {
    visibleTextRef.current = nextText;
    setVisibleText(nextText);
  };

  const clearAnimation = () => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    tokenQueueRef.current = [];
  };

  const scheduleFlush = () => {
    if (timerRef.current !== null) {
      return;
    }

    timerRef.current = window.setTimeout(flushQueue, tickMs);
  };

  const flushQueue = () => {
    timerRef.current = null;

    if (tokenQueueRef.current.length === 0) {
      return;
    }

    const backlog = tokenQueueRef.current.length;
    const tokensPerTickForSmallerBacklog = backlog > 40 ? 4 : 1;
    const tokensPerTick = backlog > 120 ? 8 : tokensPerTickForSmallerBacklog;
    const nextText = tokenQueueRef.current.splice(0, tokensPerTick).join("");

    setVisibleStreamedText(visibleTextRef.current + nextText);

    if (tokenQueueRef.current.length > 0) {
      scheduleFlush();
    }
  };

  const enqueueText = (nextText: string) => {
    if (nextText.length === 0) {
      return;
    }

    tokenQueueRef.current.push(...splitIntoTextTokens(nextText));
    scheduleFlush();
  };

  useEffect(() => {
    if (!enabled) {
      clearAnimation();
      fullTextRef.current = text;
      setVisibleStreamedText(text);
      return;
    }

    if (text.length === 0) {
      clearAnimation();
      fullTextRef.current = "";
      setVisibleStreamedText("");
      return;
    }

    const previousFullText = fullTextRef.current;

    if (text === previousFullText) {
      return;
    }

    fullTextRef.current = text;

    if (text.startsWith(previousFullText)) {
      enqueueText(text.slice(previousFullText.length));
      return;
    }

    clearAnimation();
    setVisibleStreamedText(text);
  }, [enabled, text]);

  useEffect(() => {
    return () => {
      clearAnimation();
    };
  }, []);

  return visibleText;
}
